import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    ExclamationTriangleIcon,
    ShieldExclamationIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";

import { getFailedActions, getSensitiveActions } from "../api/auditApi";
import type { FailedActionsQueryParams, SensitiveActionsQueryParams } from "../types";
import {
    AUDIT_STATUS_CLASSES,
    formatAdminIdentity,
    formatAuditDate,
    formatAuditLabel,
    formatAuditRole,
} from "../audit-utils";

// ============================================================================
// Helpers
// ============================================================================

const dayOptions = [7, 14, 30, 60, 90];

// ============================================================================
// Component
// ============================================================================

export const SecurityAuditRoute = () => {
    // ── Failed Actions State ──
    const [failedParams, setFailedParams] = useState<FailedActionsQueryParams>({
        days: 30,
        page: 0,
        size: 15,
    });

    // ── Sensitive Actions State ──
    const [sensitiveParams, setSensitiveParams] = useState<SensitiveActionsQueryParams>({
        days: 30,
        page: 0,
        size: 15,
    });

    // ── Queries ──
    const failedQ = useQuery({
        queryKey: ["audit-failed-actions", failedParams],
        queryFn: () => getFailedActions(failedParams),
    });

    const sensitiveQ = useQuery({
        queryKey: ["audit-sensitive-actions", sensitiveParams],
        queryFn: () => getSensitiveActions(sensitiveParams),
    });

    const failedData = failedQ.data?.data;
    const sensitiveData = sensitiveQ.data?.data;

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-8">
            {/* ======================================================== */}
            {/* FAILED ACTIONS                                            */}
            {/* ======================================================== */}
            <section className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <ShieldExclamationIcon className="h-5 w-5 text-rose-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            Failed Actions
                        </h3>
                        {failedData && (
                            <span className="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                                {failedData.totalElements}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400">
                            Past
                        </label>
                        <select
                            value={failedParams.days}
                            onChange={(e) =>
                                setFailedParams((p) => ({
                                    ...p,
                                    days: Number(e.target.value),
                                    page: 0,
                                }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            {dayOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n} days
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {failedQ.isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                        </div>
                    ) : failedQ.error ? (
                        <div className="px-5 py-8 text-center text-sm text-rose-500">
                            <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6" />
                            Failed to load failed actions
                        </div>
                    ) : !failedData?.logs?.length ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">
                            No failed actions found
                        </p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                                <tr>
                                    <th className="px-4 py-3 text-left">Action</th>
                                    <th className="px-4 py-3 text-left">Admin</th>
                                    <th className="px-4 py-3 text-left">Category</th>
                                    <th className="px-4 py-3 text-left">Resource</th>
                                    <th className="px-4 py-3 text-left">Request</th>
                                    <th className="px-4 py-3 text-left">Reason</th>
                                    <th className="px-4 py-3 text-left">Outcome</th>
                                    <th className="px-4 py-3 text-left">IP</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {failedData.logs.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/audit-logs/logs/${item.id}`}
                                                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                            >
                                                {formatAuditLabel(item.action)}
                                            </Link>
                                            <p className="mt-0.5 text-xs text-slate-400">
                                                {formatAuditRole(item.adminRole)}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {formatAdminIdentity(item.adminEmail, item.adminId)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {formatAuditLabel(item.actionCategory)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                                {formatAuditLabel(item.resourceType)}
                                            </span>
                                            {item.resourceId ? (
                                                <p className="mt-0.5 max-w-[150px] truncate font-mono text-[10px] text-slate-400">
                                                    {item.resourceId}
                                                </p>
                                            ) : null}
                                            {item.targetUserEmail || item.targetUserType ? (
                                                <p className="mt-1 max-w-[180px] truncate text-[10px] text-slate-400">
                                                    {item.targetUserEmail || formatAuditLabel(item.targetUserType)}
                                                </p>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                                            <p className="font-medium">{item.httpMethod || "—"}</p>
                                            <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-slate-400">
                                                {item.endpoint || "Endpoint unavailable"}
                                            </p>
                                            {item.durationMs != null ? (
                                                <p className="mt-1 text-[10px] text-slate-400">{item.durationMs} ms</p>
                                            ) : null}
                                        </td>
                                        <td className="max-w-[240px] px-4 py-3 text-xs text-rose-600 dark:text-rose-400">
                                            {item.errorMessage || item.changeSummary || "No failure detail returned"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col items-start gap-1.5">
                                                <span
                                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${AUDIT_STATUS_CLASSES[item.status] ?? AUDIT_STATUS_CLASSES.FAILURE
                                                        }`}
                                                >
                                                    {formatAuditLabel(item.status)}
                                                </span>
                                                {item.responseStatus != null ? (
                                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                                        HTTP {item.responseStatus}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                                            {item.ipAddress || "—"}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {formatAuditDate(item.performedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {failedData && failedData.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            Page {failedData.currentPage + 1} of {failedData.totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={failedData.currentPage === 0}
                                onClick={() =>
                                    setFailedParams((p) => ({ ...p, page: p.page! - 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Previous
                            </button>
                            <button
                                disabled={failedData.currentPage + 1 >= failedData.totalPages}
                                onClick={() =>
                                    setFailedParams((p) => ({ ...p, page: p.page! + 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* ======================================================== */}
            {/* SENSITIVE ACTIONS                                         */}
            {/* ======================================================== */}
            <section className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <LockClosedIcon className="h-5 w-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            Sensitive Actions
                        </h3>
                        {sensitiveData && (
                            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {sensitiveData.totalElements}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400">
                            Past
                        </label>
                        <select
                            value={sensitiveParams.days}
                            onChange={(e) =>
                                setSensitiveParams((p) => ({
                                    ...p,
                                    days: Number(e.target.value),
                                    page: 0,
                                }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            {dayOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n} days
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {sensitiveQ.isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                        </div>
                    ) : sensitiveQ.error ? (
                        <div className="px-5 py-8 text-center text-sm text-rose-500">
                            <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6" />
                            Failed to load sensitive actions
                        </div>
                    ) : !sensitiveData?.logs?.length ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">
                            No sensitive actions found
                        </p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                                <tr>
                                    <th className="px-4 py-3 text-left">Action</th>
                                    <th className="px-4 py-3 text-left">Admin</th>
                                    <th className="px-4 py-3 text-left">Category</th>
                                    <th className="px-4 py-3 text-left">Resource</th>
                                    <th className="px-4 py-3 text-left">Summary</th>
                                    <th className="px-4 py-3 text-left">Request</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {sensitiveData.logs.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/audit-logs/logs/${item.id}`}
                                                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                            >
                                                {formatAuditLabel(item.action)}
                                            </Link>
                                            <p className="mt-0.5 text-xs text-slate-400">
                                                {formatAuditRole(item.adminRole)}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {formatAdminIdentity(item.adminEmail, item.adminId)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {formatAuditLabel(item.actionCategory)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            <span className="font-medium">{formatAuditLabel(item.resourceType)}</span>
                                            {item.resourceId ? (
                                                <p className="mt-0.5 max-w-[150px] truncate font-mono text-[10px] text-slate-400">
                                                    {item.resourceId}
                                                </p>
                                            ) : null}
                                            {item.targetUserEmail || item.targetUserType ? (
                                                <p className="mt-1 max-w-[180px] truncate text-[10px] text-slate-400">
                                                    {item.targetUserEmail || formatAuditLabel(item.targetUserType)}
                                                </p>
                                            ) : null}
                                        </td>
                                        <td className="max-w-[220px] px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {item.changeSummary || item.errorMessage || "No summary provided"}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                                            <p className="font-medium">{item.httpMethod || "—"}</p>
                                            <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-slate-400">
                                                {item.endpoint || "Endpoint unavailable"}
                                            </p>
                                            {item.durationMs != null ? (
                                                <p className="mt-1 text-[10px] text-slate-400">{item.durationMs} ms</p>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col items-start gap-1.5">
                                                <span
                                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${AUDIT_STATUS_CLASSES[item.status] ?? AUDIT_STATUS_CLASSES.SUCCESS
                                                        }`}
                                                >
                                                    {formatAuditLabel(item.status)}
                                                </span>
                                                {item.responseStatus != null ? (
                                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                                        HTTP {item.responseStatus}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {formatAuditDate(item.performedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {sensitiveData && sensitiveData.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            Page {sensitiveData.currentPage + 1} of {sensitiveData.totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={sensitiveData.currentPage === 0}
                                onClick={() =>
                                    setSensitiveParams((p) => ({ ...p, page: p.page! - 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Previous
                            </button>
                            <button
                                disabled={sensitiveData.currentPage + 1 >= sensitiveData.totalPages}
                                onClick={() =>
                                    setSensitiveParams((p) => ({ ...p, page: p.page! + 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};
